import React, { useState, useEffect, RefObject, ChangeEvent } from 'react';

export type UseVideoPlayer = {
  isMuted: boolean;
  isPlaying: boolean;
  canPlay: boolean;
  hasEnded: boolean;
  progress: number;
  currentTime: string;
  togglePlay: () => void;
  handleRestart: () => void;
  handleOnTimeUpdate: (e: React.ChangeEvent<HTMLVideoElement>) => void;
  toggleMute: () => void;
  handleRequestFullscreen: () => void;
  handleSeek: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSeekStart: () => void;
  handleSeekEnd: () => void;
};

export const getFormattedTime = (value: number) => {
  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60);

  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export const useVideoPlayer = (videoElement: RefObject<HTMLVideoElement>): UseVideoPlayer => {
  const [isSeeking, setIsSeeking] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [canPlay, setCanPlay] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<string>('00:00');

  useEffect(() => {
    if (videoElement.current) {
      // use onloadedmetadata instead of oncanplay to ensure compatibility with safari on iPhones
      videoElement.current.onloadedmetadata = () => setCanPlay(true);
    }
  }, [videoElement]);

  useEffect(() => {
    if (videoElement.current && !isSeeking && canPlay) {
      videoElement.current.currentTime = (videoElement.current.duration / 100) * progress;
    }
  }, [isSeeking]);

  useEffect(() => {
    if (videoElement.current && isSeeking) {
      setCurrentTime(getFormattedTime((videoElement.current.duration / 100) * progress));
    }
  }, [isSeeking, progress]);

  useEffect(() => {
    if (videoElement.current) {
      isMuted ? (videoElement.current.muted = true) : (videoElement.current.muted = false);
    }
  }, [isMuted, videoElement]);

  const togglePlay = () => {
    // videoElement.current.play must be called directly here to satisfy safari. Calling .play() as part of a useEffect() will not work in safari
    if (videoElement.current?.paused && !videoElement.current?.ended) {
      videoElement.current?.play();
      return setIsPlaying(true);
    }
    videoElement.current?.pause();
    setIsPlaying(false);
  };

  const handleRestart = () => {
    setProgress(0);
    setIsPlaying(true);
    videoElement.current?.play();
  };

  const handleOnTimeUpdate = (e: React.ChangeEvent<HTMLVideoElement>) => {
    if (videoElement.current && !isSeeking) {
      const newProgress = (e.target.currentTime / videoElement.current.duration) * 100;
      setProgress(newProgress);
      setCurrentTime(getFormattedTime(e.target.currentTime));
    }
  };

  const handleSeekStart = () => {
    setIsSeeking(true);
  };

  const handleSeek = (event: ChangeEvent<HTMLInputElement>) => {
    const newProgress = Number(event.target.value);
    setProgress(newProgress);
  };

  const handleSeekEnd = () => {
    setIsSeeking(false);
  };

  const handleRequestFullscreen = () => {
    if (videoElement.current) {
      // @ts-ignore webkitEnterFullScreen might exist in safari browsers and is required for ios fullscreen support
      videoElement.current.requestFullscreen?.() || videoElement.current.webkitEnterFullScreen?.();
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  return {
    isMuted,
    isPlaying,
    canPlay,
    hasEnded: videoElement.current?.ended || false,
    progress,
    currentTime,
    togglePlay,
    handleRestart,
    handleOnTimeUpdate,
    toggleMute,
    handleRequestFullscreen,
    handleSeek,
    handleSeekStart,
    handleSeekEnd,
  };
};
